<template>
  <div class="flex flex-col gap-8 md:gap-6">
    <div>
      <h4 class="text-racq-dark-grey text-xl-bold">{{ titles.primary }}</h4>
      <h5 v-if="titles.secondary" class="text-racq-med-grey text-xl">{{ titles.secondary }}</h5>
      <h6 v-if="titles.tertiary" class="text-racq-med-grey text-lg mt-1">{{ titles.tertiary }}</h6>
    </div>

    <div class="premium-section">
      <span v-if="premium" class="text-racq-med-grey text-xl4-bold">{{ toCurrency(premium) }}</span>
      <span v-if="premiumInfo.frequencyLabel" class="text-racq-med-grey text-m ml-[4px]">
        {{ premiumInfo.frequencyLabel }}
      </span>
      <div v-if="showPaymentFrequencyDropdown" class="mt-3">
        <PaymentFrequencyDropdown outerClass="" />
      </div>
    </div>

    <div v-if="detailsTitle || detailsDescription" class="text-block text-racq-dark-grey">
      <div v-if="detailsTitle">
        <p class="text-base-bold">{{ detailsTitle }}</p>
      </div>
      <div v-if="detailsDescription">
        <RichText :field="{ value: detailsDescription }" class="text-base" />
      </div>
    </div>

    <div v-if="detailsDescriptionText" class="text-block text-racq-dark-grey">
      <RichText :field="{ value: detailsDescriptionText }" class="text-base" />
    </div>

    <InformationBox v-if="infoBoxType" :type="infoBoxType" :description="infoBoxMessage" />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { toCurrency } from '@/utils/convert';
import { replaceBookmarks } from '@/utils/bookmark.replace';
import { usePaymentStore } from '@/store';
import { useI18n } from 'vue-i18n';
import { PaymentFrequency } from '@/enums/payment-frequency';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import PaymentFrequencyDropdown from '@/components/PaymentFrequencyDropdown/PaymentFrequencyDropdown.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';

interface PaymentHeaderTitle {
  primary: string;
  secondary?: string;
  tertiary?: string;
}

interface PaymentHeaderPremium {
  monthly?: number;
  annual?: number;
  frequencyLabel?: string;
  adminFee?: number;
  adminFeeAnnual?: number;
  discount?: {
    monthly?: number;
    annual?: number;
  };
}

interface StepFormInputs {
  paymentHeader?: {
    DetailsTitleMonthly?: { value?: string };
    DetailsTitleAnnual?: { value?: string };
    DetailsDescriptionMonthly?: { value?: string };
    DetailsDescriptionMonthlyNoDiscount?: { value?: string };
    DetailsDescriptionAnnual?: { value?: string };
    DetailsDescriptionAnnualNoDiscount?: { value?: string };
    InfoType?: { value?: InformationBoxTypes };
    InfoboxDescription?: { value?: string };
    InfoboxDescriptionAnnually?: { value?: string };
    DetailsDescriptionText?: { value?: string };
  };
}

interface PaymentHeaderProps {
  titles: PaymentHeaderTitle;
  premiumInfo: PaymentHeaderPremium;
  stepFormInputs: StepFormInputs;
  showPaymentFrequencyDropdown?: boolean;
  overrideInfoBoxType?: InformationBoxTypes | null;
}

const props = defineProps<PaymentHeaderProps>();

const paymentStore = usePaymentStore();
const { getLocaleMessage } = useI18n();
const dictionary = getLocaleMessage('en') as Record<string, string>;

const isAnnual = computed(() => paymentStore.selectedPaymentFrequency === PaymentFrequency.Annually);
const premium = computed(() => (isAnnual.value ? props.premiumInfo.annual : props.premiumInfo.monthly));

const getFormValue = (field?: { value?: string }) => field?.value || '';

const detailsDescriptionText = computed(() => getFormValue(props.stepFormInputs.paymentHeader?.DetailsDescriptionText));

const detailsTitle = computed(() => {
  const value = isAnnual.value
    ? getFormValue(props.stepFormInputs.paymentHeader?.DetailsTitleAnnual)
    : getFormValue(props.stepFormInputs.paymentHeader?.DetailsTitleMonthly);

  return value
    ? replaceBookmarks(value, {
        annualPremium: toCurrency(props.premiumInfo.annual) ?? '',
        monthlyPremium: toCurrency(props.premiumInfo.monthly) ?? '',
        onlineDiscount: getDiscountCopy(),
      })
    : '';
});

const detailsDescription = computed(() => {
  let value = isAnnual.value
    ? getFormValue(props.stepFormInputs.paymentHeader?.DetailsDescriptionAnnual)
    : getFormValue(props.stepFormInputs.paymentHeader?.DetailsDescriptionMonthly);

  // Annually without online discount, e.g FTTP
  if (isAnnual.value && !props.premiumInfo?.discount?.annual) {
    value = getFormValue(props.stepFormInputs.paymentHeader?.DetailsDescriptionAnnualNoDiscount);
  }

  // Monthly no discount, e.g FTTP
  if (!isAnnual.value && !props.premiumInfo?.discount?.monthly) {
    value = getFormValue(props.stepFormInputs.paymentHeader?.DetailsDescriptionMonthlyNoDiscount);
  }

  return value
    ? replaceBookmarks(value, {
        annualPremium: toCurrency(props.premiumInfo.annual) ?? '',
        monthlyPremium: toCurrency(props.premiumInfo.monthly) ?? '',
        onlineDiscount: getDiscountCopy(),
        adminFee: toCurrency(props.premiumInfo.adminFee) ?? '',
        adminFeeAnnual: toCurrency(props.premiumInfo.adminFeeAnnual) ?? '',
        instalmentFee: toCurrency(props.premiumInfo.adminFee) ?? '', //backward content compatability
      })
    : '';
});

const infoBoxType = computed(() => {
  if (props.overrideInfoBoxType !== undefined) {
    return props.overrideInfoBoxType ?? undefined;
  }
  return props.stepFormInputs.paymentHeader?.InfoType?.value ?? undefined;
});

const infoBoxMessage = computed(() => {
  const value = isAnnual.value
    ? getFormValue(props.stepFormInputs.paymentHeader?.InfoboxDescriptionAnnually)
    : getFormValue(props.stepFormInputs.paymentHeader?.InfoboxDescription);

  return value
    ? replaceBookmarks(value, {
        annualPremium: toCurrency(props.premiumInfo.annual) ?? '',
        monthlyPremium: toCurrency(props.premiumInfo.monthly) ?? '',
        recurringText: paymentStore.selectedRenewalPreference ? ' a recurring' : 'an',
        adminFee: toCurrency(props.premiumInfo.adminFee) ?? '',
        adminFeeAnnual: toCurrency(props.premiumInfo.adminFeeAnnual) ?? '',
        instalmentFee: toCurrency(props.premiumInfo.adminFee) ?? '', //backward content compatability
      })
    : '';
});

function getDiscountCopy() {
  if (!props.premiumInfo?.discount) return '';
  return isAnnual.value
    ? dictionary.IQ_OnlineDiscount.replace('$#value#', toCurrency(props.premiumInfo.discount.annual, false))
    : dictionary.IQ_OnlineDiscount.replace('$#value#', toCurrency(props.premiumInfo.discount.monthly, false));
}
</script>
